/**
 * Badge that displays "NEW" for titles added within a configurable time window
 *
 * @example
 * ```tsx
 * <NewBadge createdAt={new Date("2025-10-01")} />
 * ```
 */

import type { ReactElement } from "react";
interface NewBadgeProps {
  /** ISO timestamp of when the title was created */
  readonly createdAt: string | Date;
  /** Number of days to show the badge (default 30) */
  readonly daysWindow?: number;
  /** Additional CSS classes */
  readonly className?: string;
}

const NEW_RELEASES_DAYS = 30;

export function NewBadge({
  createdAt,
  daysWindow = NEW_RELEASES_DAYS,
  className = "",
}: NewBadgeProps): ReactElement | null {
  const created = typeof createdAt === "string" ? new Date(createdAt) : createdAt;
  const now = new Date();
  const daysSinceCreation = Math.floor((now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24));

  // Don't show badge if title is older than threshold
  if (daysSinceCreation > daysWindow) {
    return null;
  }

  return (
    <span
      className={`absolute top-2 right-2 bg-lumi-accent text-black px-2 py-1 text-[10px] font-semibold uppercase rounded shadow-2xl z-20 ${className}`}
      aria-label={`New content added ${daysSinceCreation} day${daysSinceCreation === 1 ? "" : "s"} ago`}
    >
      NEW
    </span>
  );
}
